import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useWebsiteSettings from '../hooks/useWebsiteSettings';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const { settings } = useWebsiteSettings();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: '/', key: 'home' },
    { path: '/about', key: 'about' },
    { path: '/portfolio', key: 'portfolio' },
    { path: '/partners', key: 'partners' },
    { path: '/careers', key: 'careers' },
    { path: '/contact', key: 'contact' }
  ];

  // Different styling for home page vs other pages
  const navBackground = isHomePage 
    ? (isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent')
    : 'bg-white shadow-sm';

  // Logo selection based on page and scroll state
  const logoSrc = settings.logoUrl 
    ? settings.logoUrl
    : (isHomePage 
        ? (isScrolled ? "/STAPOTECHPRIMARY SMALL logo groen png.png" : "/STAPOTECHPRIMARY SMALL logo wite png.png")
        : "/STAPOTECHPRIMARY SMALL logo groen png.png");

  const textColor = isHomePage
    ? (isScrolled ? 'text-gray-900' : 'text-white')
    : 'text-gray-900';

  const textColorInactive = isHomePage
    ? (isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white')
    : 'text-gray-600 hover:text-gray-900';

  const borderColor = isHomePage
    ? (isScrolled ? 'border-gray-900' : 'border-white')
    : 'border-gray-900';



  const mobileButtonColor = isHomePage
    ? (isScrolled ? 'text-gray-900' : 'text-white')
    : 'text-gray-900';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBackground}`}>
      <div className="container mx-auto px-6 py-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src={logoSrc} 
              alt="Stapotech Logo" 
              className="h-12" 
            />
            <span className={`text-xl font-light tracking-wider ${textColor}`}>
              STAPOTECH
            </span>
          </Link>
          
          <div className="hidden lg:flex items-center space-x-12">
            {navItems.map(item => (
              <Link
                key={item.key}
                to={item.path}
                className={`text-sm font-light tracking-wider uppercase transition-all duration-300 pb-1 ${
                  isActive(item.path)
                    ? `${textColor} border-b ${borderColor}`
                    : textColorInactive
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
            
            <LanguageSwitcher />
          </div>

          <button
            className={`lg:hidden ${mobileButtonColor}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-8 py-8 border-t border-gray-200">
            {navItems.map(item => (
              <Link
                key={item.key}
                to={item.path}
                className={`block w-full text-left py-4 text-sm font-light tracking-wider uppercase transition-colors ${
                  isActive(item.path)
                    ? 'text-gray-900 font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
            <div className="mt-6">
              <LanguageSwitcher />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;